<script setup lang="ts">
import {
  firstAllowedDate,
  isDateAllowed,
  lastAllowedDate,
  normalizeDate,
  overriddedExcludedDates,
  overriddedIncludedDates,
  seasonEnd,
  seasonStart,
  type DateRange,
} from '@/elements/dates';
import { nl } from 'date-fns/locale';

import VueDatePicker from '@vuepic/vue-datepicker';

import '@vuepic/vue-datepicker/dist/main.css';

const date = defineModel<Date>('date', { required: true, default: () => firstAllowedDate.value });

const props = defineProps<{
  allowedRanges: DateRange[];
}>();

function isDateDisabled(date: Date): boolean {
  const normalizedDate = normalizeDate(date);

  const excludedDates = overriddedExcludedDates.value;
  const isExcluded = excludedDates.some((excludedDate) => normalizedDate.getTime() === excludedDate.getTime());
  if (isExcluded) {
    return true;
  }

  const includedDates = overriddedIncludedDates.value;
  const isIncluded = includedDates.some((includedDate) => normalizedDate.getTime() === includedDate.getTime());
  if (isIncluded) {
    return false;
  }

  return !isDateAllowed(date, props.allowedRanges);
}
</script>

<template>
  <VueDatePicker
    v-model="date"
    :enable-time-picker="false"
    :auto-apply="true"
    :min-start-date="seasonStart"
    :max-end-date="seasonEnd"
    :min-date="firstAllowedDate"
    :max-date="lastAllowedDate"
    :clearable="false"
    format="d MMMM yyyy"
    :format-locale="nl"
    :disabled-dates="isDateDisabled"
    prevent-min-max-navigation
    locale="nl"
    timezone="Europe/Amsterdam"
    required
    v-bind="$attrs"
  >
    <template #input-icon>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon">
        <g id="airplane-plane-big 1">
          <path
            id="Vector"
            d="M9.99899 9.5L7.13371 4.497C7.04662 4.34493 7.00101 4.17265 7.00147 3.9974C7.00192 3.82216 7.04842 3.65011 7.13631 3.4985C7.22419 3.34688 7.35038 3.22103 7.50222 3.13354C7.65406 3.04605 7.82623 3 8.00147 3H9.11434C9.24606 3 9.37647 3.02602 9.4981 3.07656C9.61972 3.12711 9.73017 3.20118 9.82308 3.29453L16 9.5"
            stroke="black"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            id="Vector_2"
            d="M9.07987 14L6.11987 19.528C6.03829 19.6804 5.9976 19.8513 6.00175 20.024C6.00591 20.1968 6.05476 20.3656 6.14356 20.5138C6.23236 20.6621 6.35807 20.7848 6.50843 20.87C6.65878 20.9552 6.82865 21 7.00148 21H8.1623C8.29458 21 8.42556 20.9737 8.54763 20.9228C8.6697 20.8718 8.78044 20.7971 8.87344 20.703L15.5001 14"
            stroke="black"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            id="Vector_3"
            d="M8.99996 14H4.55482C4.13507 14 3.72595 13.8679 3.38543 13.6225C3.04491 13.3771 2.79024 13.0307 2.6575 12.6325L1.05205 7.81623C1.00194 7.66591 0.988274 7.50583 1.01217 7.34919C1.03606 7.19255 1.09684 7.04383 1.18949 6.91529C1.28214 6.78675 1.40401 6.68206 1.54505 6.60986C1.68609 6.53765 1.84228 6.5 2.00073 6.5H3.43519C3.70135 6.5 3.96271 6.57082 4.19246 6.70519C4.42221 6.83956 4.61206 7.03263 4.74255 7.26461L5.99996 9.5H20.75C21.3467 9.5 21.919 9.73705 22.341 10.159C22.7629 10.581 23 11.1533 23 11.75C23 12.3467 22.7629 12.919 22.341 13.341C21.919 13.7629 21.3467 14 20.75 14H15.5"
            stroke="black"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </svg>
    </template>
  </VueDatePicker>
</template>

<style lang="scss">
.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #656565;
  --dp-hover-color: #e3e3e3;
  --dp-hover-text-color: #656565;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #1a4c8d;
  --dp-primary-disabled-color: #6bacea;
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: #e3e3e3;
  --dp-menu-border-color: #e3e3e3;
  --dp-border-color-hover: #e3e3e3;
  --dp-border-color-focus: #e3e3e3;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #e3e3e3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #e3e3e3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #656565);
  --dp-range-between-border-color: var(--dp-hover-color, #e3e3e3);

  * {
    font-weight: 600;
  }
}
.dp__input_icon {
  padding-left: 24px;
  height: 24px;
}

.dp__input_reg {
  color: #000;
  font-feature-settings:
    'clig' off,
    'liga' off;

  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 157%;

  border-radius: 4px !important;
  padding: 16px 24px 16px 54px !important;
  background: var(--grey-dark, #eee) !important;
}

.icon {
  align-self: center;
}
</style>
